"use client";

import { ArrowRight, ScanFace, FileCheck2, Brain } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Separator } from "@/components/ui/separator";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function UseCases() {
  const header = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const grid = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const cards = [{
    icon: ScanFace,
    title: "On-site diagnostics",
    description: "Comprehensive field analysis of industrial equipment, utilizing precision sensors to identify mechanical wear and performance bottlenecks.",
    capabilities: "Vibration analysis · Thermal imaging · Real-time reporting"
  }, {
    icon: FileCheck2,
    title: "Certified maintenance",
    description: "Standardized service protocols for Siemens hardware, ensuring compliance with global safety and operational efficiency standards.",
    capabilities: "OEM parts usage · Calibration logs · Safety certification"
  }, {
    icon: Brain,
    title: "Predictive analytics",
    description: "Data-driven insights that forecast component failure before it occurs, minimizing downtime and optimizing your maintenance schedule.",
    capabilities: "Trend forecasting · Load monitoring · Adaptive scheduling"
  }];
  return <section data-section-id="20275" id="use-cases" className="bg-background text-foreground py-24 md:py-36">
      <div className="mx-auto w-full max-w-[1240px] px-5 md:px-8">
        {}
        <div ref={header.ref} className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 transition-all duration-700 ease-out" style={{
        opacity: header.isVisible ? 1 : 0,
        transform: header.isVisible ? "translateY(0)" : "translateY(16px)"
      }}>
          <div className="lg:col-span-5">
            <p className="font-default text-xs font-medium uppercase tracking-widest text-muted-foreground">
              Our core services
            </p>
            <Separator className="mt-5 mb-7 bg-border" />
            <h2 className="font-default text-[2.125rem] md:text-[2.75rem] lg:text-[3.25rem] leading-[1.1] font-bold text-foreground">
              Three pillars,{" "}
              <em className="italic font-normal">
                total
              </em>{" "}
              reliability
            </h2>
          </div>

          <div className="lg:col-span-7 lg:pt-2">
            <p className="font-default text-[1.0625rem] md:text-[1.1875rem] leading-[1.55] font-normal text-muted-foreground">
              We provide a comprehensive suite of field services designed to keep your industrial operations running at peak performance. From precision diagnostics to predictive maintenance, our expert team ensures your infrastructure remains reliable and efficient.
            </p>
            <div className="mt-6">
              <Link to="/FieldServicesPage" className="inline-flex items-center gap-2 font-default text-sm font-medium underline underline-offset-4 decoration-1 transition-colors duration-150 text-primary">
                <span>Explore our services</span>
                <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
              </Link>
            </div>
          </div>
        </div>

        {}
        <div ref={grid.ref} className="mt-16 md:mt-24 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
          {cards.map((card, i) => {
          const Icon = card.icon;
          return <div key={i} data-index={i} className="group relative p-8 transition-all duration-300 ease-out bg-card border border-border rounded-lg shadow-sm" style={{
            opacity: grid.isVisible ? 1 : 0,
            transform: grid.isVisible ? "translateY(0)" : "translateY(16px)",
            transitionDelay: `${i * 80}ms`,
            transitionProperty: "opacity, transform, border-color"
          }} onMouseEnter={e => {
            e.currentTarget.style.borderColor = "var(--primary)";
            const edge = e.currentTarget.querySelector("[data-edge]") as HTMLElement | null;
            if (edge) edge.style.opacity = "1";
          }} onMouseLeave={e => {
            e.currentTarget.style.borderColor = "var(--border)";
            const edge = e.currentTarget.querySelector("[data-edge]") as HTMLElement | null;
            if (edge) edge.style.opacity = "0";
          }}>
                {}
                <span data-edge aria-hidden="true" className="absolute left-0 top-0 bottom-0 transition-opacity duration-300 ease-out bg-primary" style={{
              width: "4px",
              opacity: 0
            }} />

                <Icon className="w-6 h-6 text-primary" strokeWidth={1.5} />

                <h3 className="font-default mt-7 text-[1.5rem] font-semibold leading-[1.25] text-foreground">
                  {card.title}
                </h3>

                <p className="font-default mt-4 text-[0.9375rem] font-normal leading-[1.6] text-muted-foreground">
                  {card.description}
                </p>

                <Separator className="mt-8 mb-5 bg-border" />

                <p className="font-default text-[0.8125rem] font-medium leading-[1.4] text-muted-foreground">
                  {card.capabilities}
                </p>
              </div>;
        })}
        </div>
      </div>
    </section>;
}